import { Component, OnDestroy } from '@angular/core';
import {
  Auth,
  sendEmailVerification,
  verifyBeforeUpdateEmail,
} from 'firebase/auth';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Inject } from '@angular/core';
import { AUTH } from 'src/app/firebase-injection';
import { LocalStorageService } from 'src/app/services/local-storage.service';

@Component({
  selector: 'app-verify-email',
  templateUrl: './verify-email.component.html',
  styleUrl: './verify-email.component.scss',
  imports: [ReactiveFormsModule],
})
export class VerifyEmailComponent implements OnDestroy {
  private static readonly RESEND_COOLDOWN_SECONDS = 30;
  private static readonly COOLDOWN_STORAGE_PREFIX = 'verifyMailLastSentAt:';

  checkForVerifiedInterval: number;
  resendCooldownSeconds = 0;
  private resendCooldownInterval?: number;
  private isSendingVerification = false;
  mailForm: FormGroup = new FormGroup({
    mail: new FormControl('', [Validators.required, Validators.email]),
  });

  constructor(
    @Inject(AUTH) private auth: Auth,
    private router: Router,
    private _snackBar: MatSnackBar,
    private localStorage: LocalStorageService
  ) {
    this.checkForVerifiedInterval = window.setInterval(() => {
      this.checkForVerified();
    }, 1000);
    this.auth.onAuthStateChanged((user) => {
      if (user) {
        this.mailForm.setValue({ mail: user.email ?? '' });
        this.restoreCooldownForUser(user.uid);
      }
    });
  }

  ngOnDestroy(): void {
    clearInterval(this.checkForVerifiedInterval);
    if (this.resendCooldownInterval !== undefined) {
      clearInterval(this.resendCooldownInterval);
    }
  }

  checkForVerified() {
    if (this.auth.currentUser) {
      this.auth.currentUser.reload().then(() => {
        if (this.auth.currentUser?.emailVerified) {
          clearInterval(this.checkForVerifiedInterval);
          const pendingInvite = this.localStorage.getInviteLink();
          if (pendingInvite) {
            this.localStorage.setInviteLink(undefined);
            this.router.navigateByUrl(pendingInvite);
          } else {
            this.router.navigate(['/app']);
          }
        }
      });
    }
  }

  changeMail() {
    if (!this.mailForm.valid) {
      return;
    }
    const user = this.auth.currentUser;
    const email = this.mailForm.value.mail;
    if (!user || !email || email === user.email) {
      return;
    }
    verifyBeforeUpdateEmail(user, email).then(() => {
      this._snackBar.open(
        'Verification mail sent to new address. Logging out.',
        'Close',
        { duration: 3000 }
      );
      this.auth.signOut().then(() => this.router.navigate(['/landing']));
    });
  }

  logout() {
    this.auth.signOut().then(() => {
      this.router.navigate(['/landing']);
    });
  }

  protected sendMailVerify() {
    const user = this.auth.currentUser;
    if (!user || user.emailVerified) {
      return;
    }
    if (this.isSendingVerification || this.resendCooldownSeconds > 0) {
      return;
    }
    this.isSendingVerification = true;
    sendEmailVerification(user)
      .then(() => {
        this._snackBar.open('Email Verification Sent', 'Close', {
          duration: 3000,
        });
        this.persistCooldown(user.uid);
        this.startResendCooldownTicker(
          VerifyEmailComponent.RESEND_COOLDOWN_SECONDS
        );
      })
      .finally(() => {
        this.isSendingVerification = false;
      });
  }

  private restoreCooldownForUser(uid: string): void {
    const lastSent = Number(
      localStorage.getItem(
        VerifyEmailComponent.COOLDOWN_STORAGE_PREFIX + uid
      ) ?? 0
    );
    if (!lastSent) {
      return;
    }
    const elapsedSeconds = (Date.now() - lastSent) / 1000;
    const remaining = Math.ceil(
      VerifyEmailComponent.RESEND_COOLDOWN_SECONDS - elapsedSeconds
    );
    if (remaining > 0) {
      this.startResendCooldownTicker(remaining);
    }
  }

  private persistCooldown(uid: string): void {
    localStorage.setItem(
      VerifyEmailComponent.COOLDOWN_STORAGE_PREFIX + uid,
      String(Date.now())
    );
  }

  private startResendCooldownTicker(initialSeconds: number): void {
    this.resendCooldownSeconds = initialSeconds;
    if (this.resendCooldownInterval !== undefined) {
      clearInterval(this.resendCooldownInterval);
    }
    this.resendCooldownInterval = window.setInterval(() => {
      this.resendCooldownSeconds -= 1;
      if (this.resendCooldownSeconds <= 0) {
        this.resendCooldownSeconds = 0;
        if (this.resendCooldownInterval !== undefined) {
          clearInterval(this.resendCooldownInterval);
          this.resendCooldownInterval = undefined;
        }
      }
    }, 1000);
  }
}
